// Pure calculation utilities for Parlay Assistant.
// All math is deterministic and side-effect-free so it can be unit-tested.

const EPS = 1e-9;

/**
 * Validate an American-odds value.
 * Rules: integer, not zero, |odds| >= 100.
 */
export function isValidAmerican(value) {
  if (value === null || value === undefined || value === "") return false;
  const n = Number(value);
  if (!Number.isFinite(n)) return false;
  if (!Number.isInteger(n)) return false;
  if (n === 0) return false;
  if (n > -100 && n < 100) return false;
  return true;
}

/**
 * Human-readable validation error message for an American odds input.
 * Returns "" when the value is valid.
 */
export function americanError(raw) {
  if (raw === "" || raw === null || raw === undefined) {
    return "Enter odds like -110 or +150.";
  }
  const trimmed = String(raw).trim();
  if (!/^[+-]?\d+$/.test(trimmed)) {
    return "Use whole numbers only (e.g. -110 or +150).";
  }
  const n = Number(trimmed);
  if (n === 0) return "Odds cannot be zero.";
  if (n > -100 && n < 100) {
    return "American odds must be +100 or higher, or -100 or lower.";
  }
  return "";
}

/**
 * Convert American odds to decimal odds.
 * +N -> 1 + N/100
 * -N -> 1 + 100/|N|
 */
export function americanToDecimal(american) {
  const n = Number(american);
  if (!isValidAmerican(n)) throw new Error("Invalid American odds");
  return n > 0 ? 1 + n / 100 : 1 + 100 / Math.abs(n);
}

/**
 * Convert decimal odds to American odds (rounded to nearest whole).
 * dec >= 2.00 -> (dec - 1) * 100
 * dec <  2.00 -> -100 / (dec - 1)
 */
export function decimalToAmerican(dec) {
  const d = Number(dec);
  if (!Number.isFinite(d) || d <= 1) throw new Error("Invalid decimal odds");
  const raw = d >= 2 ? (d - 1) * 100 : -100 / (d - 1);
  return Math.round(raw);
}

/**
 * Convert decimal odds to implied probability percentage.
 */
export function decimalToImpliedProbability(dec) {
  const d = Number(dec);
  if (!Number.isFinite(d) || d <= 0) throw new Error("Invalid decimal odds");
  return (1 / d) * 100;
}

/**
 * Greatest common divisor for positive integers.
 */
function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b > 0) {
    [a, b] = [b, a % b];
  }
  return a || 1;
}

/**
 * Find the best rational approximation of a positive real number x
 * with a denominator bounded by maxDen using continued fractions.
 * Returns { num, den } in lowest terms.
 */
function bestRational(x, maxDen = 1000) {
  if (x < 0) throw new Error("bestRational expects non-negative input");
  let a = Math.floor(x);
  let h1 = 1;
  let k1 = 0;
  let h = a;
  let k = 1;
  let frac = x - a;
  let safety = 0;
  while (frac > 1e-10 && safety < 64) {
    const inv = 1 / frac;
    a = Math.floor(inv);
    const h2 = a * h + h1;
    const k2 = a * k + k1;
    if (k2 > maxDen) break;
    h1 = h;
    k1 = k;
    h = h2;
    k = k2;
    frac = inv - a;
    safety += 1;
  }
  const g = gcd(h, k);
  return { num: h / g, den: k / g };
}

/**
 * Convert decimal odds to a reduced fractional-odds representation "num/den".
 */
export function decimalToFractional(dec) {
  const d = Number(dec);
  if (!Number.isFinite(d) || d <= 1) throw new Error("Invalid decimal odds");
  const net = d - 1;
  const { num, den } = bestRational(net, 1000);
  return { num, den, text: `${num}/${den}` };
}

/**
 * Parse and validate a fractional-odds string like "10/11" or "3/2".
 */
export function parseFractional(raw) {
  if (raw === "" || raw === null || raw === undefined) return null;
  const trimmed = String(raw).trim();
  const match = /^(\d+)\s*\/\s*(\d+)$/.exec(trimmed);
  if (!match) return null;
  const num = Number(match[1]);
  const den = Number(match[2]);
  if (!Number.isFinite(num) || !Number.isFinite(den)) return null;
  if (den === 0) return null;
  if (num === 0) return null;
  return { num, den };
}

export function fractionalError(raw) {
  if (raw === "" || raw === null || raw === undefined) {
    return "Enter odds like 3/2 or 10/11.";
  }
  const parsed = parseFractional(raw);
  if (!parsed) return "Use the format numerator/denominator, e.g. 3/2.";
  if (parsed.den <= 0) return "Denominator must be greater than zero.";
  if (parsed.num <= 0) return "Numerator must be greater than zero.";
  return "";
}

export function fractionalToDecimal(num, den) {
  if (den === 0) throw new Error("Denominator cannot be zero");
  return 1 + num / den;
}

export function decimalError(raw) {
  if (raw === "" || raw === null || raw === undefined) {
    return "Enter decimal odds like 1.91 or 2.50.";
  }
  const trimmed = String(raw).trim();
  if (!/^\d*\.?\d+$/.test(trimmed)) {
    return "Use a positive number like 1.91 or 2.50.";
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return "Enter a valid number.";
  if (n <= 1) return "Decimal odds must be greater than 1.00.";
  return "";
}

/**
 * Combine an array of valid American-odds legs into a full parlay result.
 * All legs must be valid; returns null if any are invalid.
 */
export function computeParlay(betAmount, americanLegs) {
  const bet = Number(betAmount);
  if (!Number.isFinite(bet) || bet < 0) return null;
  if (!Array.isArray(americanLegs) || americanLegs.length < 2) return null;
  for (const leg of americanLegs) {
    if (!isValidAmerican(leg)) return null;
  }
  const decimals = americanLegs.map((a) => americanToDecimal(Number(a)));
  const combinedDecimal = decimals.reduce((acc, d) => acc * d, 1);
  const combinedAmerican = decimalToAmerican(combinedDecimal);
  const impliedProbability = decimalToImpliedProbability(combinedDecimal);
  const totalPayout = bet * combinedDecimal;
  const profit = totalPayout - bet;
  return {
    combinedDecimal,
    combinedAmerican,
    impliedProbability,
    totalPayout,
    profit,
    bet,
  };
}

/**
 * Format a number as US dollars with two decimal places.
 */
export function formatUSD(amount) {
  if (!Number.isFinite(Number(amount))) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount));
}

/**
 * Format American odds with an explicit sign.
 */
export function formatAmerican(am) {
  const n = Math.round(Number(am));
  if (!Number.isFinite(n)) return "";
  return n > 0 ? `+${n}` : `${n}`;
}

/**
 * Format decimal odds to two decimal places.
 */
export function formatDecimal(dec) {
  const n = Number(dec);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2);
}

/**
 * Format an implied-probability number (already a percent) to two decimals.
 */
export function formatPercent(p) {
  const n = Number(p);
  if (!Number.isFinite(n)) return "";
  return `${n.toFixed(2)}%`;
}

// ---------------------------------------------------------------------------
// Round Robin math
// ---------------------------------------------------------------------------

/**
 * n choose r (binomial coefficient), computed iteratively to avoid overflow.
 * Returns 0 for invalid inputs (n < 0, r < 0, r > n).
 */
export function nCr(n, r) {
  if (!Number.isInteger(n) || !Number.isInteger(r)) return 0;
  if (n < 0 || r < 0 || r > n) return 0;
  const k = Math.min(r, n - r);
  let result = 1;
  for (let i = 0; i < k; i += 1) {
    result = (result * (n - i)) / (i + 1);
  }
  return Math.round(result);
}

/**
 * Generate all r-sized combinations of indices [0..n-1] in lexicographic order.
 * Each combination is an array of r ascending indices.
 */
export function generateCombinations(n, r) {
  if (!Number.isInteger(n) || !Number.isInteger(r)) return [];
  if (r < 0 || r > n || n <= 0) return [];
  if (r === 0) return [[]];
  const out = [];
  const stack = new Array(r);
  const walk = (start, depth) => {
    if (depth === r) {
      out.push(stack.slice());
      return;
    }
    // We need (r - depth) more items; ensure enough remain.
    const limit = n - (r - depth);
    for (let i = start; i <= limit; i += 1) {
      stack[depth] = i;
      walk(i + 1, depth + 1);
    }
  };
  walk(0, 0);
  return out;
}

/**
 * Leg status values used by the Round Robin grader.
 */
export const LEG_STATUS = Object.freeze({
  NOT_DECIDED: "not_decided",
  WON: "won",
  LOST: "lost",
  PUSH: "push",
  VOID: "void",
});

/**
 * Distribute a total budget across `count` combinations so every combination
 * receives a value in whole cents and the FULL budget is used exactly.
 * The last combination absorbs any remaining pennies from integer division.
 *
 * Examples:
 *   distributeBudget(10, 3)   -> [3.33, 3.33, 3.34]
 *   distributeBudget(0.10, 3) -> [0.03, 0.03, 0.04]
 */
export function distributeBudget(budget, count) {
  if (!Number.isFinite(budget) || budget < 0) return [];
  if (!Number.isInteger(count) || count <= 0) return [];
  const totalCents = Math.round(budget * 100);
  const base = Math.floor(totalCents / count);
  const remainder = totalCents - base * count;
  const out = new Array(count);
  for (let i = 0; i < count; i += 1) out[i] = base / 100;
  // Absorb the leftover pennies into the LAST combination.
  if (remainder > 0) {
    out[count - 1] = (base + remainder) / 100;
  }
  return out;
}

/**
 * Grade a single combination given its leg statuses and per-leg decimal odds.
 * Rules:
 *  - PUSH and VOID legs drop out of the combination.
 *  - If every valid (non-push/void) leg is dropped, the combo is refunded
 *    (return = stake, profit = 0, status = "pushed").
 *  - If ANY valid leg is LOST, the combo is lost (return = 0).
 *  - If all valid legs are WON, the combo wins at the product of WON leg odds.
 *  - Otherwise the combo is "pending" — potential payout is stake x product of
 *    the decimal odds of legs that are still live (WON or NOT_DECIDED).
 *
 * Returns:
 *   { status, stake, return, profit, potentialReturn, potentialProfit, effectiveDecimal }
 */
export function gradeCombination(stake, legStatuses, legDecimals) {
  const s = Number(stake);
  if (!Number.isFinite(s) || s < 0) {
    return {
      status: "invalid",
      stake: 0,
      return: 0,
      profit: 0,
      potentialReturn: 0,
      potentialProfit: 0,
      effectiveDecimal: 1,
    };
  }
  const live = [];
  let anyLost = false;
  let anyPending = false;

  for (let i = 0; i < legStatuses.length; i += 1) {
    const st = legStatuses[i];
    if (st === LEG_STATUS.PUSH || st === LEG_STATUS.VOID) continue;
    if (st === LEG_STATUS.LOST) {
      anyLost = true;
      break;
    }
    if (st === LEG_STATUS.NOT_DECIDED) anyPending = true;
    live.push(legDecimals[i]);
  }

  if (anyLost) {
    return {
      status: "lost",
      stake: s,
      return: 0,
      profit: -s,
      potentialReturn: 0,
      potentialProfit: -s,
      effectiveDecimal: 0,
    };
  }

  // Zero live legs — every leg pushed/voided. Refund stake.
  if (live.length === 0) {
    return {
      status: "pushed",
      stake: s,
      return: s,
      profit: 0,
      potentialReturn: s,
      potentialProfit: 0,
      effectiveDecimal: 1,
    };
  }

  const effectiveDecimal = live.reduce((acc, d) => acc * d, 1);
  const potentialReturn = s * effectiveDecimal;

  if (anyPending) {
    return {
      status: "pending",
      stake: s,
      return: 0,
      profit: 0,
      potentialReturn,
      potentialProfit: potentialReturn - s,
      effectiveDecimal,
    };
  }

  // All live legs won.
  return {
    status: "won",
    stake: s,
    return: potentialReturn,
    profit: potentialReturn - s,
    potentialReturn,
    potentialProfit: potentialReturn - s,
    effectiveDecimal,
  };
}

/**
 * Compute a full round-robin ticket.
 *
 * @param {object}  params
 * @param {number[]} params.americanLegs   Legs in American odds (3-12).
 * @param {number[]} params.sizes          Combination sizes to include (e.g. [2,3]).
 * @param {"per_combo"|"total_budget"} params.wagerMode
 * @param {number}  params.wagerValue      Amount per combo OR total budget (>= 0).
 * @param {string[]} [params.legStatuses]  Optional per-leg status (LEG_STATUS.*).
 * @returns {object|null} A structured result, or null on invalid input.
 */
export function computeRoundRobin({
  americanLegs,
  sizes,
  wagerMode,
  wagerValue,
  legStatuses,
}) {
  if (!Array.isArray(americanLegs) || americanLegs.length < 3 || americanLegs.length > 12) {
    return null;
  }
  if (!Array.isArray(sizes) || sizes.length === 0) return null;
  for (const l of americanLegs) {
    if (!isValidAmerican(l)) return null;
  }
  for (const k of sizes) {
    if (!Number.isInteger(k) || k < 2 || k > americanLegs.length) return null;
  }
  const value = Number(wagerValue);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (wagerMode !== "per_combo" && wagerMode !== "total_budget") return null;

  const n = americanLegs.length;
  const decimals = americanLegs.map((a) => americanToDecimal(Number(a)));
  const statuses =
    Array.isArray(legStatuses) && legStatuses.length === n
      ? legStatuses
      : new Array(n).fill(LEG_STATUS.NOT_DECIDED);

  // Build every combination first (used for both modes).
  const groups = sizes.slice().sort((a, b) => a - b).map((size) => {
    const combos = generateCombinations(n, size).map((indices) => ({
      indices,
      size,
    }));
    return { size, combos };
  });

  const totalCombos = groups.reduce((acc, g) => acc + g.combos.length, 0);
  if (totalCombos === 0) return null;

  // Allocate stakes per combination.
  let stakes;
  if (wagerMode === "per_combo") {
    stakes = new Array(totalCombos).fill(Math.round(value * 100) / 100);
  } else {
    stakes = distributeBudget(value, totalCombos);
  }

  // Walk every combination in the same order and attach stakes/grades.
  let cursor = 0;
  const groupResults = groups.map(({ size, combos }) => {
    const rows = combos.map(({ indices }) => {
      const stake = stakes[cursor];
      cursor += 1;
      const legDecimals = indices.map((i) => decimals[i]);
      const legStat = indices.map((i) => statuses[i]);
      const decimalProduct = legDecimals.reduce((acc, d) => acc * d, 1);
      const grade = gradeCombination(stake, legStat, legDecimals);
      return {
        indices,
        stake,
        decimalProduct,
        americanCombined: decimalToAmerican(decimalProduct),
        maxReturn: stake * decimalProduct,
        maxProfit: stake * decimalProduct - stake,
        grade,
      };
    });
    const subtotalStake = rows.reduce((a, r) => a + r.stake, 0);
    const subtotalMaxReturn = rows.reduce((a, r) => a + r.maxReturn, 0);
    return {
      size,
      count: rows.length,
      rows,
      subtotalStake,
      subtotalMaxReturn,
    };
  });

  const totalStake = groupResults.reduce((a, g) => a + g.subtotalStake, 0);
  const totalMaxReturn = groupResults.reduce((a, g) => a + g.subtotalMaxReturn, 0);
  const totalMaxProfit = totalMaxReturn - totalStake;

  // Aggregate grading — sum of settled returns so far and remaining potential.
  let settledReturn = 0;
  let settledStake = 0;
  let pendingReturnMax = 0;
  let pendingStake = 0;
  let comboCounts = { won: 0, lost: 0, pushed: 0, pending: 0 };
  groupResults.forEach((g) => {
    g.rows.forEach((row) => {
      const { grade } = row;
      comboCounts[grade.status] = (comboCounts[grade.status] || 0) + 1;
      if (grade.status === "won" || grade.status === "pushed") {
        settledReturn += grade.return;
        settledStake += grade.stake;
      } else if (grade.status === "lost") {
        settledStake += grade.stake;
      } else if (grade.status === "pending") {
        pendingReturnMax += grade.potentialReturn;
        pendingStake += grade.stake;
      }
    });
  });

  const settledProfit = settledReturn - settledStake;
  const overallStatus = comboCounts.pending > 0 ? "pending" : "settled";

  return {
    n,
    sizes: groups.map((g) => g.size),
    totalCombos,
    wagerMode,
    wagerValue: value,
    legDecimals: decimals,
    legStatuses: statuses,
    groups: groupResults,
    totalStake,
    totalMaxReturn,
    totalMaxProfit,
    settledReturn,
    settledStake,
    settledProfit,
    pendingReturnMax,
    pendingStake,
    comboCounts,
    overallStatus,
  };
}

export const _internal = { bestRational, gcd, EPS };
